/**
 * Wave 6b.3 — Discovery & Growth unified route.
 *
 * `/app/discovery-growth` — 6-tab panel exposing the last four skills
 * of Wave 6b (video, sms, seo-audit, marketing-ideas) plus the four
 * final skills of Wave 6b.3 (competitors, popups, ai-seo, image-extended).
 * Every tab wires a compact form → generate → list view. Bild-Varianten
 * uses the multi-format image generator. Ideen-generieren accepts a free
 * brief and returns a ranked list. All heavy backend interaction goes
 * through `api` from `lib/api.js`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Video, MessageSquare, Search, Sparkles, Users, MousePointer2,
  BookOpen, ImageIcon,
} from "lucide-react";

/* ---------- shared helpers ---------- */

function Section({ title, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  const isLlm = source === "llm";
  return (
    <Badge
      data-testid="source-badge"
      variant={isLlm ? "default" : "outline"}
      className={isLlm ? "" : "border-amber-500 text-amber-700"}
    >
      {isLlm ? "AI" : "Fallback"}
    </Badge>
  );
}

/* ═══════════════════════════ VIDEO TAB ═══════════════════════════ */

function VideoTab() {
  const [kind, setKind] = useState("reels");
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try { const r = await api.get("/creative/video/scripts");
      setItems(r.data.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    if (!topic) return toast.error("Topic fehlt.");
    setBusy(true);
    try {
      const r = await api.post("/creative/video/generate-script", {
        kind, topic, target_audience: audience });
      toast.success(`Script erstellt (${r.data.source})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-video-content">
      <Section title="Video-Script erzeugen" testid="video-form">
        <div className="space-y-2">
          <Label>Kanal</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="video-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="youtube_long">YouTube (Long)</SelectItem>
              <SelectItem value="youtube_shorts">YouTube Shorts</SelectItem>
              <SelectItem value="tiktok">TikTok</SelectItem>
              <SelectItem value="reels">Instagram Reels</SelectItem>
              <SelectItem value="video_ad">Video Ad</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Thema</Label>
          <Input value={topic} onChange={(e) => setTopic(e.target.value)}
                    data-testid="video-topic" placeholder="z.B. Neubau Wuppertal" />
        </div>
        <div className="space-y-2">
          <Label>Zielgruppe</Label>
          <Input value={audience} onChange={(e) => setAudience(e.target.value)}
                    data-testid="video-audience" placeholder="z.B. Eigentümer 45+" />
        </div>
        <Button onClick={generate} disabled={busy}
                  data-testid="video-generate">Script generieren</Button>
      </Section>
      <Section title="Zuletzt erstellt" testid="video-list">
        <ul className="text-sm space-y-1 max-h-80 overflow-auto">
          {items.map((v) => (
            <li key={v.id} className="flex items-center gap-2"
                  data-testid={`video-item-${v.id}`}>
              <Badge variant="outline">{v.kind}</Badge>
              <span className="flex-1 truncate">{v.topic}</span>
            </li>
          ))}
          {items.length === 0 && <li className="text-muted-foreground">Noch nichts.</li>}
        </ul>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ SMS TAB ═══════════════════════════ */

function SmsTab() {
  const [purpose, setPurpose] = useState("promotional");
  const [len, setLen] = useState(3);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try { const r = await api.get("/lifecycle/sms-sequences");
      setItems(r.data.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    setBusy(true);
    try {
      const r = await api.post("/lifecycle/sms-sequences/generate",
        { purpose, sequence_length: len });
      toast.success(`Sequenz erstellt (${r.data.source})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-sms-content">
      <Section title="SMS-Sequenz erzeugen" testid="sms-form">
        <div className="space-y-2">
          <Label>Zweck</Label>
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger data-testid="sms-purpose"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="promotional">Promotional</SelectItem>
              <SelectItem value="transactional">Transactional</SelectItem>
              <SelectItem value="reminder">Reminder</SelectItem>
              <SelectItem value="onboarding">Onboarding</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Länge (Schritte)</Label>
          <Input type="number" value={len} min={1} max={7}
                    onChange={(e) => setLen(parseInt(e.target.value) || 3)}
                    data-testid="sms-length" />
        </div>
        <Button onClick={generate} disabled={busy}
                  data-testid="sms-generate">Sequenz generieren</Button>
      </Section>
      <Section title="Sequenzen" testid="sms-list">
        <ul className="text-sm space-y-1 max-h-80 overflow-auto">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-2"
                  data-testid={`sms-item-${it.id}`}>
              <Badge variant="outline">{(it.steps || []).length}×</Badge>
              <span className="flex-1 truncate">{it.name}</span>
            </li>
          ))}
          {items.length === 0 && <li className="text-muted-foreground">Noch nichts.</li>}
        </ul>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ SEO TAB ═══════════════════════════ */

function SeoAuditTab() {
  const [url, setUrl] = useState("https://brimo.marketing.viona.immo/");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try { const r = await api.get("/seo/audits");
      setItems(r.data.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function audit() {
    setBusy(true);
    try {
      const r = await api.post("/seo/audit", { target_url: url, depth: "quick" });
      toast.success(`Audit fertig (${r.data.source})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-seo-content">
      <Section title="SEO-Audit" testid="seo-form">
        <div className="space-y-2">
          <Label>Ziel-URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)}
                    data-testid="seo-url" placeholder="https://..." />
        </div>
        <Button onClick={audit} disabled={busy}
                  data-testid="seo-audit-btn">Audit starten</Button>
      </Section>
      <Section title="Letzte Audits" testid="seo-list">
        <ul className="text-sm space-y-1 max-h-80 overflow-auto">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2"
                  data-testid={`seo-item-${a.id}`}>
              <Badge>{a.score_overall}</Badge>
              <span className="flex-1 truncate">{a.target_url_or_landing_page_id}</span>
            </li>
          ))}
          {items.length === 0 && <li className="text-muted-foreground">Noch nichts.</li>}
        </ul>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ IDEAS TAB (Ideen generieren) ══════════ */

function IdeasTab() {
  const [brief, setBrief] = useState("");
  const [n, setN] = useState(7);
  const [busy, setBusy] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [cid, setCid] = useState(null);

  async function generate() {
    if (!brief.trim()) return toast.error("Brief eingeben.");
    setBusy(true);
    try {
      const r = await api.post("/ai/marketing-ideas",
        { brief_context: brief, ideas_count: n });
      setIdeas(r.data.campaign_ideas.generated_ideas || []);
      setCid(r.data.campaign_ideas.id);
      toast.success(`${r.data.count} Ideen erzeugt`);
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  async function accept(ideaId, convertTo) {
    try {
      await api.post(`/ai/marketing-ideas/${cid}/accept`,
                          { idea_id: ideaId, convert_to: convertTo });
      toast.success(`Idee angenommen (${convertTo})`);
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-ideas-content">
      <Section title="Ideen generieren" testid="ideas-form">
        <div className="space-y-2">
          <Label>Brief</Label>
          <Textarea value={brief} onChange={(e) => setBrief(e.target.value)}
                        rows={6} data-testid="ideas-brief"
                        placeholder="Kontext für die Kampagne …" />
        </div>
        <div className="space-y-2">
          <Label>Anzahl Ideen (5-10)</Label>
          <Input type="number" min={5} max={10} value={n}
                    onChange={(e) => setN(parseInt(e.target.value) || 7)}
                    data-testid="ideas-count" />
        </div>
        <Button onClick={generate} disabled={busy}
                  data-testid="ideas-generate">Ideen generieren</Button>
      </Section>
      <Section title="Ergebnisse" testid="ideas-list">
        <ul className="text-sm space-y-2 max-h-80 overflow-auto">
          {ideas.map((it) => (
            <li key={it.id} className="border-b pb-2"
                  data-testid={`idea-${it.id}`}>
              <div className="font-medium">{it.title}</div>
              <div className="text-xs text-muted-foreground">
                {it.format} · {it.funnel_stage} · Impact {it.expected_impact}
              </div>
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant="outline"
                          onClick={() => accept(it.id, "campaign_draft")}
                          data-testid={`idea-accept-camp-${it.id}`}>
                  → Kampagne
                </Button>
              </div>
            </li>
          ))}
          {ideas.length === 0 && <li className="text-muted-foreground">
            Brief eingeben und „Ideen generieren“ klicken.</li>}
        </ul>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ COMPETITORS TAB ═════════════════════ */

function CompetitorsTab() {
  const [name, setName] = useState("");
  const [fmt, setFmt] = useState("vs");
  const [build, setBuild] = useState(false);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try { const r = await api.get("/lp-v2/competitor-pages");
      setItems(r.data.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    if (!name.trim()) return toast.error("Mitbewerber-Name fehlt.");
    setBusy(true);
    try {
      const r = await api.post("/lp-v2/competitor-pages/generate",
        { competitor_name: name, format: fmt, build_landing_page: build });
      toast.success(`Vergleich erstellt (${r.data.source})`
        + (r.data.landing_page_id ? ` + LP #${r.data.landing_page_id.slice(0,6)}` : ""));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-competitors-content">
      <Section title="Vergleichsseite erzeugen" testid="competitors-form">
        <div className="space-y-2">
          <Label>Mitbewerber</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="competitors-name" placeholder="z.B. Immobilienscout24" />
        </div>
        <div className="space-y-2">
          <Label>Format</Label>
          <Select value={fmt} onValueChange={setFmt}>
            <SelectTrigger data-testid="competitors-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="vs">Vs (1:1)</SelectItem>
              <SelectItem value="alternative">Alternative (singular)</SelectItem>
              <SelectItem value="alternatives_plural">Alternatives (plural)</SelectItem>
              <SelectItem value="battle_card">Battle card</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={build}
                    onChange={(e) => setBuild(e.target.checked)}
                    data-testid="competitors-build-lp" />
          Landing-Page mit erzeugen
        </label>
        <Button onClick={generate} disabled={busy}
                  data-testid="competitors-generate">Vergleich generieren</Button>
      </Section>
      <Section title="Vergleiche" testid="competitors-list">
        <ul className="text-sm space-y-1 max-h-80 overflow-auto">
          {items.map((c) => (
            <li key={c.id} className="flex items-center gap-2"
                  data-testid={`competitor-${c.id}`}>
              <Badge variant="outline">{c.format}</Badge>
              <span className="flex-1 truncate">{c.competitor_name}</span>
              {c.landing_page_id && <Badge>LP</Badge>}
            </li>
          ))}
          {items.length === 0 && <li className="text-muted-foreground">Noch nichts.</li>}
        </ul>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ POPUPS TAB ══════════════════════════ */

function PopupsTab() {
  const [lps, setLps] = useState([]);
  const [lpId, setLpId] = useState("");
  const [purpose, setPurpose] = useState("email_capture");
  const [trigger, setTrigger] = useState("exit_intent");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/lp-v2").then((r) => setLps(r.data.items || [])).catch(() => {});
  }, []);
  const load = useCallback(async () => {
    if (!lpId) return setItems([]);
    try { const r = await api.get(`/lp-v2/${lpId}/popups`);
      setItems(r.data.items || []); } catch { /* ignore */ }
  }, [lpId]);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    if (!lpId) return toast.error("LP wählen.");
    setBusy(true);
    try {
      const r = await api.post(`/lp-v2/${lpId}/popups`,
        { purpose, trigger: { kind: trigger }, active: true });
      toast.success(`Popup erstellt (${r.data.source})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  async function toggle(pid, active) {
    try {
      await api.patch(`/lp-v2/${lpId}/popups/${pid}`, { active });
      await load();
    } catch (e) { toast.error("Fehler"); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-popups-content">
      <Section title="Popup erstellen" testid="popups-form">
        <div className="space-y-2">
          <Label>Landing-Page</Label>
          <Select value={lpId} onValueChange={setLpId}>
            <SelectTrigger data-testid="popups-lp"><SelectValue placeholder="LP wählen" /></SelectTrigger>
            <SelectContent>
              {lps.map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.title || l.slug || l.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Zweck</Label>
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger data-testid="popups-purpose"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email_capture">E-Mail-Capture</SelectItem>
              <SelectItem value="announcement">Announcement</SelectItem>
              <SelectItem value="discount">Rabatt</SelectItem>
              <SelectItem value="feedback">Feedback</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Trigger</Label>
          <Select value={trigger} onValueChange={setTrigger}>
            <SelectTrigger data-testid="popups-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="exit_intent">Exit-Intent</SelectItem>
              <SelectItem value="scroll">Scroll</SelectItem>
              <SelectItem value="time">Zeit</SelectItem>
              <SelectItem value="click">Klick</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={generate} disabled={busy || !lpId}
                  data-testid="popups-generate">Popup generieren</Button>
      </Section>
      <Section title="Popups" testid="popups-list">
        <ul className="text-sm space-y-1 max-h-80 overflow-auto">
          {items.map((p) => (
            <li key={p.id} className="flex items-center gap-2"
                  data-testid={`popup-${p.id}`}>
              <Badge variant={p.active ? "default" : "outline"}>
                {p.active ? "aktiv" : "inaktiv"}
              </Badge>
              <span className="flex-1 truncate">{p.name}</span>
              <Button size="sm" variant="outline"
                        onClick={() => toggle(p.id, !p.active)}
                        data-testid={`popup-toggle-${p.id}`}>
                {p.active ? "Aus" : "An"}
              </Button>
            </li>
          ))}
          {items.length === 0 && <li className="text-muted-foreground">
            {lpId ? "Keine Popups." : "LP wählen."}</li>}
        </ul>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ AI-SEO TAB ═════════════════════════ */

function AiSeoTab() {
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try { const r = await api.get("/seo/ai-seo");
      setItems(r.data.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function optimize() {
    if (focus.length < 2) return toast.error("Fokus-Entität eingeben.");
    setBusy(true);
    try {
      const r = await api.post("/seo/ai-seo/optimize", { focus_entity: focus });
      toast.success(`AI-SEO-Empfehlungen bereit (${r.data.source})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  async function requestApproval(id) {
    try {
      const r = await api.post("/seo/ai-seo/request-approval",
                                        { optimization_id: id });
      toast.success(`Approval angefragt (#${r.data.approval_id.slice(0,6)})`);
      await load();
    } catch (e) { toast.error("Fehler"); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-ai-seo-content">
      <Section title="AI-SEO / AEO / GEO Optimierung" testid="ai-seo-form">
        <div className="space-y-2">
          <Label>Fokus-Entität (Produkt / Thema)</Label>
          <Input value={focus} onChange={(e) => setFocus(e.target.value)}
                    data-testid="ai-seo-focus"
                    placeholder="z.B. Immobilienbewertung Wuppertal" />
        </div>
        <Button onClick={optimize} disabled={busy}
                  data-testid="ai-seo-optimize">Optimierung erstellen</Button>
        <p className="text-xs text-muted-foreground">
          Empfehlungen sind approval-gated. Erst nach Approval kann „Apply&#8220; ausgeführt werden.
        </p>
      </Section>
      <Section title="Empfehlungen" testid="ai-seo-list">
        <ul className="text-sm space-y-2 max-h-80 overflow-auto">
          {items.map((o) => (
            <li key={o.id} className="border-b pb-2"
                  data-testid={`ai-seo-item-${o.id}`}>
              <div className="flex items-center gap-2">
                <Badge variant={o.status === "applied" ? "default" : "outline"}>
                  {o.status}
                </Badge>
                <span className="flex-1 truncate">{o.focus_entity}</span>
                {o.status === "proposed" && (
                  <Button size="sm"
                            onClick={() => requestApproval(o.id)}
                            data-testid={`ai-seo-request-${o.id}`}>
                    Approval anfragen
                  </Button>
                )}
              </div>
            </li>
          ))}
          {items.length === 0 && <li className="text-muted-foreground">Noch nichts.</li>}
        </ul>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ IMAGE-EXTENDED TAB (Bild-Varianten) ═══ */

const DEFAULT_FORMATS = ["hero_16x9", "og_image", "instagram_post_1x1"];
const ALL_FORMATS = [
  "hero_1x1", "hero_16x9", "hero_9x16", "og_image",
  "instagram_post_1x1", "instagram_story_9x16",
  "facebook_ad_1_91x1", "linkedin_post_1_91x1",
  "twitter_16x9", "tiktok_9x16",
];

function ImageVariantsTab() {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("");
  const [selected, setSelected] = useState(new Set(DEFAULT_FORMATS));
  const [busy, setBusy] = useState(false);
  const [variants, setVariants] = useState([]);

  function toggle(k) {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  }

  async function generate() {
    if (prompt.length < 3) return toast.error("Prompt zu kurz.");
    if (selected.size === 0) return toast.error("Format wählen.");
    setBusy(true);
    try {
      const r = await api.post("/creative/image/generate-multi-format",
        { prompt, formats: [...selected], style: style || undefined });
      setVariants(r.data.variants || []);
      toast.success(
        `${r.data.count} Varianten erstellt`
        + (r.data.any_fallback ? " (Pillow-Fallback)" : ""));
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Fehler"); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tab-image-variants-content">
      <Section title="Bild-Varianten erzeugen" testid="image-form">
        <div className="space-y-2">
          <Label>Prompt</Label>
          <Textarea rows={4} value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        data-testid="image-prompt"
                        placeholder="z.B. Skyline Wuppertal Sonnenuntergang, cinematic …" />
        </div>
        <div className="space-y-2">
          <Label>Stil (optional)</Label>
          <Input value={style} onChange={(e) => setStyle(e.target.value)}
                    data-testid="image-style" placeholder="cinematic, editorial …" />
        </div>
        <div className="space-y-1">
          <Label>Formate ({selected.size})</Label>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {ALL_FORMATS.map((k) => (
              <label key={k} className="flex items-center gap-1">
                <input type="checkbox" checked={selected.has(k)}
                          onChange={() => toggle(k)}
                          data-testid={`image-fmt-${k}`} />
                {k}
              </label>
            ))}
          </div>
        </div>
        <Button onClick={generate} disabled={busy}
                  data-testid="image-generate">
          Multi-Format generieren
        </Button>
      </Section>
      <Section title="Varianten" testid="image-list">
        <div className="grid grid-cols-2 gap-2 max-h-96 overflow-auto">
          {variants.map((v) => (
            <div key={v.id} className="border rounded-md overflow-hidden"
                  data-testid={`variant-${v.format}`}>
              <img src={v.asset_url} alt={v.format}
                     className="w-full h-24 object-cover bg-muted" />
              <div className="p-1 text-xs">
                <div className="flex items-center justify-between">
                  <span>{v.format}</span>
                  {v.is_fallback && <Badge variant="outline"
                                                    className="border-amber-500 text-amber-700">
                                                Fallback</Badge>}
                </div>
                <div className="text-muted-foreground">
                  {v.dimensions.w}×{v.dimensions.h}
                </div>
              </div>
            </div>
          ))}
          {variants.length === 0 && (
            <div className="col-span-2 text-sm text-muted-foreground">
              Prompt + Formate wählen und „Multi-Format generieren&#8220;.
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════════════ PAGE ═══════════════════════════ */

export default function DiscoveryGrowth() {
  const t = useT();
  return (
    <div className="space-y-4" data-testid="discovery-growth-page">
      <header>
        <h1 className="text-2xl font-semibold">
          {t("discovery.title", "Discovery & Growth")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("discovery.subtitle",
              "Video, SMS, SEO, AI-SEO, Wettbewerb, Popups und Bild-Varianten "
              + "— die letzten 8 Skills auf einem Panel.")}
        </p>
      </header>
      <Tabs defaultValue="video" className="space-y-4">
        <TabsList data-testid="discovery-tabs" className="flex-wrap">
          <TabsTrigger value="video" data-testid="tab-video">
            <Video className="mr-1 size-4" /> Video
          </TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms">
            <MessageSquare className="mr-1 size-4" /> SMS
          </TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">
            <Search className="mr-1 size-4" /> SEO
          </TabsTrigger>
          <TabsTrigger value="ai-seo" data-testid="tab-ai-seo">
            <Sparkles className="mr-1 size-4" /> AI-SEO
          </TabsTrigger>
          <TabsTrigger value="competitors" data-testid="tab-competitors">
            <Users className="mr-1 size-4" /> Wettbewerb
          </TabsTrigger>
          <TabsTrigger value="popups" data-testid="tab-popups">
            <MousePointer2 className="mr-1 size-4" /> Popups
          </TabsTrigger>
          <TabsTrigger value="ideas" data-testid="tab-ideas">
            <BookOpen className="mr-1 size-4" /> Ideen
          </TabsTrigger>
          <TabsTrigger value="images" data-testid="tab-images">
            <ImageIcon className="mr-1 size-4" /> Bild-Varianten
          </TabsTrigger>
        </TabsList>
        <TabsContent value="video"><VideoTab /></TabsContent>
        <TabsContent value="sms"><SmsTab /></TabsContent>
        <TabsContent value="seo"><SeoAuditTab /></TabsContent>
        <TabsContent value="ai-seo"><AiSeoTab /></TabsContent>
        <TabsContent value="competitors"><CompetitorsTab /></TabsContent>
        <TabsContent value="popups"><PopupsTab /></TabsContent>
        <TabsContent value="ideas"><IdeasTab /></TabsContent>
        <TabsContent value="images"><ImageVariantsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
